import React from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { cn } from "../utils";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";
import "./license-plate-input.css";

/** Props Interface */
interface LicensePlateInputProps<T extends FieldValues> extends ExtendedInputWrapperProps<T> {
	control: Control<T>;
	name: Path<T>;
}

const placeholders = {
	"0": "BER",
	"1": "HX",
	"2": "1234",
} as const;

const maxLengths = {
	"0": 3,
	"1": 2,
	"2": 4,
} as const;

export function LicensePlateInput<T extends FieldValues>({
	control,
	name,
	isDisabled,
	...props
}: LicensePlateInputProps<T>) {
	/** Functions */
	function handleOnKeyDown(_: React.KeyboardEvent<HTMLInputElement>) {}
	function handleOnKeyUp(_: React.KeyboardEvent<HTMLInputElement>, _maxLength: number) {}
	function handleOnFocus(_: React.FocusEvent<HTMLInputElement>) {}

	/** Render */
	return (
		<InputWrapper
			errors={[] as any}
			name={`name.${0}`}
			isDisabled={isDisabled}
			{...props}
		>
			<div className={cn("flex flex-row gap-1 input pl-5 pr-1 w-max relative overflow-hidden")}>
				<div className="absolute inset-y-0 left-0 bg-linear-0 from-blue-600 to-blue-500 w-3 flex-center flex-col gap-x-2 px-1" />
				{(["0", "1", "2"] as const).map((value) => (
					<Controller
						key={value}
						control={control}
						name={`${name}.${value}` as Path<T>}
						render={({ field }) => (
							<input
								className={cn("text-2xl leading-none tracking-wide")}
								onChange={(e) => {
									e.target.value = e.target.value.trim();
									field.onChange(e);
								}}
								onKeyDown={handleOnKeyDown}
								onFocus={handleOnFocus}
								onKeyUp={(e) => handleOnKeyUp(e, maxLengths[value])}
								value={field.value}
								disabled={field.disabled || isDisabled}
								placeholder={placeholders[value]}
								maxLength={maxLengths[value]}
								style={{
									fontFamily: "GermanLicensePlate",
								}}
								size={field.value?.length || maxLengths[value]}
								data-cy={`license-plate-input-${value}`}
							/>
						)}
					/>
				))}
			</div>
		</InputWrapper>
	);
}
